import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { TreeDeciduous, FileCheck, ChevronLeft, ChevronRight, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NurserySection = () => {
  const [species, setSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSpecies, setSelectedSpecies] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadSpecies();
  }, []);

  const loadSpecies = async () => {
    try {
      const response = await axios.get(`${API}/public/nursery`);
      setSpecies(response.data);
    } catch (error) {
      console.error('Error loading nursery:', error);
    } finally {
      setLoading(false);
    }
  };

  const openGallery = (item) => {
    setSelectedSpecies(item);
    setCurrentImageIndex(0);
  };

  const closeGallery = () => {
    setSelectedSpecies(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedSpecies && selectedSpecies.images) {
      setCurrentImageIndex((prev) => 
        prev === selectedSpecies.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedSpecies && selectedSpecies.images) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedSpecies.images.length - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-b from-emerald-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <TreeDeciduous className="h-4 w-4" />
            El Nostre Viver
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Palmeres amb Passaport Fitosanitari
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Descobreix les nostres espècies de palmeres de qualitat, totes amb certificació fitosanitària oficial
          </p>
        </div>

        {species.length === 0 ? (
          <div className="text-center py-12">
            <TreeDeciduous className="h-16 w-16 mx-auto text-emerald-300 mb-4" />
            <p className="text-slate-500">Properament mostrarem les nostres espècies de palmeres disponibles</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {species.map((item) => (
            <Card 
              key={item._id || item.id} 
              className="border-0 shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group"
              onClick={() => openGallery(item)}
            >
              <div className="relative h-56 bg-slate-100 overflow-hidden">
                {item.images && item.images.length > 0 ? (
                  <img
                    src={item.images[0]}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <TreeDeciduous className="h-16 w-16 text-slate-300" />
                  </div>
                )}
                {item.images && item.images.length > 1 && (
                  <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded">
                    {item.images.length} fotos
                  </span>
                )}
                {item.has_phytosanitary_passport && (
                  <span className="absolute top-3 left-3 bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg">
                    <FileCheck className="h-3 w-3" />
                    Passaport Fitosanitari
                  </span>
                )}
                {!item.availability && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold">
                      No disponible
                    </span>
                  </div>
                )}
              </div>
              <CardContent className="p-5">
                <h3 className="text-xl font-bold text-slate-900 mb-1">{item.name}</h3>
                {item.scientific_name && (
                  <p className="text-sm text-slate-500 italic mb-3">{item.scientific_name}</p>
                )}
                <p className="text-slate-600 text-sm line-clamp-2 mb-4">{item.description}</p>
                <div className="flex flex-wrap gap-2">
                  {item.height_range && (
                    <span className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-xs">
                      📏 {item.height_range}
                    </span>
                  )}
                  {item.price_range && (
                    <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-medium">
                      {item.price_range}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        )}
      </div>

      {/* Gallery Modal */}
      <Dialog open={!!selectedSpecies} onOpenChange={closeGallery}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="text-xl">{selectedSpecies?.name}</DialogTitle>
            {selectedSpecies?.scientific_name && (
              <p className="text-sm text-slate-500 italic">{selectedSpecies.scientific_name}</p>
            )}
          </DialogHeader>
          
          {selectedSpecies && selectedSpecies.images && selectedSpecies.images.length > 0 && (
            <div className="relative">
              <div className="aspect-[16/10] bg-slate-900">
                <img
                  src={selectedSpecies.images[currentImageIndex]}
                  alt={`${selectedSpecies.name} - Imatge ${currentImageIndex + 1}`}
                  className="w-full h-full object-contain"
                />
              </div>
              
              {selectedSpecies.images.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {selectedSpecies.images.map((_, index) => (
                      <button
                        key={index}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                        }`}
                        onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          
          <div className="p-4 space-y-4">
            <p className="text-slate-700">{selectedSpecies?.description}</p>
            <div className="flex flex-wrap gap-3">
              {selectedSpecies?.height_range && (
                <span className="bg-slate-100 text-slate-700 px-4 py-2 rounded-lg text-sm">
                  📏 Alçada: {selectedSpecies.height_range}
                </span>
              )}
              {selectedSpecies?.price_range && (
                <span className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-lg text-sm font-medium">
                  💰 {selectedSpecies.price_range}
                </span>
              )}
              {selectedSpecies?.has_phytosanitary_passport && (
                <span className="bg-green-100 text-green-700 px-4 py-2 rounded-lg text-sm flex items-center gap-1">
                  <FileCheck className="h-4 w-4" />
                  Passaport Fitosanitari
                </span>
              )}
              {selectedSpecies?.availability ? (
                <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg text-sm">
                  ✓ Disponible
                </span>
              ) : (
                <span className="bg-red-100 text-red-700 px-4 py-2 rounded-lg text-sm">
                  ✗ No disponible
                </span>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default NurserySection;
